<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { toast } from "@/common/utils/toast";

import { maskCEP } from "@/common/utils/mask";

import { baseInput, baseSelect } from "@/common/components";

import { searchByCep } from "@/common/api/rest/address";

import type { AddressType } from "@/common/types/common";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: AddressType | null;
    title?: string;
    width?: string;
  }>(),
  {
    width: "100%",
  }
);

const defaultData = {
  is_main: false,
  name: "",
  address: "",
  number: "",
  complement: "",
  district: "",
  city: "",
  state: "",
  country: "Brasil",
  cep: "",
};

const payload = ref<AddressType>({ ...defaultData });

const countryOptions = computed(() => [{ option: "Brasil", value: "Brasil" }]);

function update() {
  emit("update-data", payload.value);
}

function searchCep() {
  searchByCep(payload.value.cep)
    .then((response) => {
      if (!response) {
        toast({ message: t("error.default"), type: "error" });
        return;
      }
      const { bairro, localidade, logradouro, uf } = response;

      payload.value = {
        ...payload.value,
        address: logradouro,
        state: uf,
        district: bairro,
        city: localidade,
      };
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function cepMask(value: string) {
  if (payload.value.country === "Brasil") return maskCEP(value);

  return value;
}

watchEffect(() => {
  if (props.data) {
    payload.value = { ...props.data };
  }
});
</script>

<template>
  <section class="form-content" :style="{ width: width }">
    <div v-if="title" class="title">
      {{ title }}
    </div>
    <form>
      <base-input
        v-model="payload.name"
        :value="payload.name"
        @input="update()"
      >
        <template #label> {{ t("common.labels.name") }} * </template>
      </base-input>

      <div class="flex flex-row gap-2">
        <base-select
          v-model="payload.country"
          :value="payload.country"
          :options="countryOptions"
          @change="update()"
        >
          <template #label> {{ t("common.labels.country") }} * </template>
        </base-select>

        <base-input
          v-model="payload.cep"
          :value="payload.cep"
          :mask="cepMask"
          @input="update()"
          @blur="searchCep()"
        >
          <template #label> {{ t("common.labels.cep") }} * </template>
        </base-input>
      </div>

      <div class="flex flex-row gap-2">
        <div class="flex-2">
          <base-input
            v-model="payload.address"
            :value="payload.address"
            @input="update()"
          >
            <template #label> {{ t("common.labels.address") }} * </template>
          </base-input>
        </div>

        <div class="flex-1">
          <base-input
            v-model="payload.number"
            :value="payload.number"
            @input="update()"
          >
            <template #label> {{ t("common.labels.number") }} * </template>
          </base-input>
        </div>
      </div>

      <div class="flex flex-row gap-2">
        <base-input
          v-model="payload.complement"
          :value="payload.complement"
          @input="update()"
        >
          <template #label>
            {{ t("common.labels.complement") }}
          </template>
        </base-input>

        <base-input
          v-model="payload.district"
          :value="payload.district"
          @input="update()"
        >
          <template #label> {{ t("common.labels.district") }} * </template>
        </base-input>
      </div>

      <div class="flex flex-row gap-2">
        <base-input
          v-model="payload.city"
          :value="payload.city"
          @input="update()"
        >
          <template #label> {{ t("common.labels.city") }} * </template>
        </base-input>

        <base-input
          v-model="payload.state"
          :value="payload.state"
          @input="update()"
        >
          <template #label> {{ t("common.labels.state") }} * </template>
        </base-input>
      </div>
    </form>
  </section>
</template>

<style lang="scss" scoped>
.form-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.5rem;
    font-weight: bold;
    text-transform: uppercase;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}
</style>